import { useState } from "react";
import {
  Checkbox,
  Combobox,
  Group,
  Input,
  Pill,
  PillsInput,
  ScrollArea,
  useCombobox,
} from "@mantine/core";
import styled from "styled-components";
import ChevronDownIcon from "../Icons/ChevronDownIcon";
import { motion } from "framer-motion";
import { theme } from "../../../theme";

const MAX_DISPLAYED_VALUES = 1;

export function CustomMultiSelect({
  data,
  setSelectedValues,
  inputLabel,
  placeholder,
  value: controlledValue,
  onChange: controlledOnChange,
  ...rest
}: any) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setIsDropdownOpen(false);
    },
    onDropdownOpen: () => {
      if (!rest.disabled) {
        combobox.updateSelectedOptionIndex("active");
        setIsDropdownOpen(true);
      }
    },
  });

  const isControlled =
    controlledValue !== undefined && controlledOnChange !== undefined;

  const [internalValue, setInternalValue] = useState<string[]>([]);
  const value = isControlled ? controlledValue : internalValue;
  const setValue = isControlled ? controlledOnChange : setInternalValue;

  const handleValueSelect = (val: string) => {
    const newValue = value.includes(val)
      ? value.filter((v: any) => v !== val)
      : [...value, val];

    setValue(newValue);

    if (setSelectedValues) {
      const currentValues = Array.isArray(setSelectedValues)
        ? setSelectedValues
        : [];
      if (currentValues.includes(val)) {
        setSelectedValues(currentValues.filter((item) => item !== val));
      } else {
        setSelectedValues([...currentValues, val]);
      }
    }
  };

  const handleValueRemove = (val: string) => {
    const newValue = value.filter((v: any) => v !== val);
    setValue(newValue);

    if (setSelectedValues) {
      setSelectedValues((prev: any) =>
        Array.isArray(prev) ? prev.filter((item) => item !== val) : []
      );
    }
  };

 const values = value
  ?.slice(
    0,
    MAX_DISPLAYED_VALUES === value.length
      ? MAX_DISPLAYED_VALUES
      : MAX_DISPLAYED_VALUES - 1
  )
  .map((val: string) => {
    const selectedOption = data?.find((option: any) => {
      const optionValue = String(option.value ?? option._id);
      return optionValue === String(val);
    });
    const label = selectedOption?.label ?? selectedOption?.name;
    return selectedOption ? <div key={val}>{label}</div> : null;
  });

const optionsList = data?.map((item: any) => {
  const valueKey = String(item.value ?? item._id);
  const labelKey = item.label ?? item.name;

  return (
    <Combobox.Option
      value={valueKey}
      key={valueKey}
      active={value.includes(valueKey)}
    >
      <Group gap="sm">
        <Checkbox
          size="16px"
          checked={value.includes(valueKey)}
          onChange={() => handleValueSelect(valueKey)}
          aria-hidden
          tabIndex={-1}
          color="#5C26FF"
          style={{ pointerEvents: "none" }}
        />
        <span>{labelKey}</span>
      </Group>
    </Combobox.Option>
  );
});


  return (
    <div>
      <Combobox
        store={combobox}
        onOptionSubmit={(val) => handleValueSelect(val)}
        withinPortal={false}
      >
        <Combobox.DropdownTarget>
          <StyledPillsInput
            $isFocus={isDropdownOpen}
            rightSection={
              <motion.div
                animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <ChevronDownIcon size={20} />
              </motion.div>
            }
            label={inputLabel}
            w={240}
            pointer
            onClick={() => {
              combobox.toggleDropdown();
            }}
            {...rest}
          >
            <Pill.Group>
              {value.length > 0 ? (
                <>
                  {values}
                  {value.length > MAX_DISPLAYED_VALUES && (
                    <div>
                      {value.length - (MAX_DISPLAYED_VALUES - 1)} adet seçili.
                    </div>
                  )}
                </>
              ) : (
                <Input.Placeholder className="label">
                  {placeholder}
                </Input.Placeholder>
              )}

              <Combobox.EventsTarget>
                <PillsInput.Field
                  type="hidden"
                  onBlur={() => combobox.closeDropdown()}
                  onKeyDown={(event) => {
                    if (event.key === "Backspace") {
                      event.preventDefault();
                      handleValueRemove(value[value.length - 1]);
                    }
                  }}
                />
              </Combobox.EventsTarget>
            </Pill.Group>
          </StyledPillsInput>
        </Combobox.DropdownTarget>

        <Combobox.Dropdown h={"max-content"}>
          <ScrollArea h={data?.length < 5 ? "max-content" : 130}>
            <Combobox.Options>
              <OptionListItem>{optionsList}</OptionListItem>
            </Combobox.Options>
          </ScrollArea>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  );
}

const OptionListItem = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  color: #1e1e1e;
`;

const StyledPillsInput = styled(PillsInput)<{ $isFocus: boolean }>`
  .mantine-PillsInput-input {
    display: flex;
    height: 44px;
    border-color: ${({ $isFocus }) =>
      $isFocus ? theme.colors.brand.normal : "none"};
    box-shadow: ${({ $isFocus }) =>
      $isFocus ? "0px 0px 5px 3px #5c26ff14" : "none"};
    &[data-disabled="true"] {
      background-color: #f9fafb;
      color: black;
      cursor: not-allowed;
      opacity: 1;
    }
  }

  .label {
    color: ${theme.colors.greyscale.gray60};
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
  }
`;
